import * as yup from "yup";

import { BoxProps, Button, Divider, Flex, Image, Text } from "@chakra-ui/react";
import { AppForm, FormField, Link } from "@picovico/ui";
import { useRegisterUserMutation } from "store/services/auth";

import {
  catchErrorHandler,
  parseApiError,
  storeLocalStorageData,
} from "@picovico/utils";
import { useRouter } from "next/router";
import { useAppToast } from "@/hooks/useAppToast";
import { useSocialSignIn } from "@/hooks/useSocialSignIn";
import { useVisibleSocialProviders } from "@/hooks/useWebFeature";
import { resetUserStateAction } from "@/store/slices";
import { useDispatch } from "react-redux";
import { getPageRoutes } from "@/routes/pageRoutes";

export interface registerValues {
  name: string;
  email: string;
  mobile: string;
  // password: string;
  // confirm_password: string;
  [key: string]: string;
}

const registerFormValidationSchema = yup.object().shape({
  name: yup.string().required().label("Name"),
  email: yup.string().email().required().label("Email"),
  mobile: yup
    .string()
    .matches(
      /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
      "Mobile number is not valid",
    )
    .length(10, "Mobile number should be 10 digits"),
  // password: yup.string().required().label("Password"),
  // confirm_password: yup
  //   .string()
  //   .oneOf(
  //     [yup.ref("password")],
  //     "Confirm password doesn't match with password",
  //   )
  //   .label("Confirm Password"),
});

const initialValues: registerValues = {
  name: "",
  email: "",
  mobile: "",
  // password: "",
  // confirm_password: "",
};

type SocialAuthProvider = { providerName: string; providerSvg: string };

interface RegisterFormProps extends BoxProps {
  socialAuthProviders?: SocialAuthProvider[];
}

function RegisterForm2({
  socialAuthProviders: configuredSocialProviders,
  ...props
}: RegisterFormProps) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { error: errorToast, success } = useAppToast();
  const [register, { isLoading, error }] = useRegisterUserMutation({});
  const startSocialSignIn = useSocialSignIn();
  // Google is hidden while the company has google_signin off.
  const socialAuthProviders = useVisibleSocialProviders(
    configuredSocialProviders,
  );
  return (
    <AppForm<registerValues>
      apiError={(error && parseApiError(error)) || null}
      formProps={{
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        justifyContent: "center",
        mt: { base: "0rem", md: "1.5rem" },
        ...props,
      }}
      initialValues={initialValues}
      validationSchema={registerFormValidationSchema}
      onSubmit={(values) => {
        register(values)
          .unwrap()
          .then((res) => {
            if (!res) return;
            res?.data?.message && success(res.data.message);
            if (res?.data.token) {
              storeLocalStorageData("token", res.data.token);
              storeLocalStorageData("device_id", res.data.device_id);
            }
            dispatch(resetUserStateAction());
            const nextUrl = router.query.next as string;
            if (nextUrl) {
              const target = decodeURIComponent(nextUrl);
              if (target.startsWith("/")) {
                router.push(target);
              } else {
                router.push(getPageRoutes(target));
              }
            } else {
              router.push(getPageRoutes("home"));
            }
          })
          .catch((err) => {
            catchErrorHandler(err, errorToast);
          });
      }}
    >
      <FormField
        name="name"
        placeholder="Enter your name"
        rounded={"50px"}
        padding={"25px"}
        backgroundColor="#FFFFFF"
        border={"1px solid #E2F4FF"}
      />

      <FormField
        name="email"
        placeholder="Enter your email address"
        rounded={"50px"}
        padding={"25px"}
        backgroundColor="#FFFFFF"
        border={"1px solid #E2F4FF"}
      />
      <FormField
        name="mobile"
        type="tel"
        placeholder="Enter your mobile number"
        rounded={"50px"}
        padding={"25px"}
        backgroundColor="#FFFFFF"
        border={"1px solid #E2F4FF"}
      />

      {/* <FormField name="password" type="password" label="Password" required />
      <FormField
        name="confirm_password"
        type="password"
        label="Confirm Password"
        required
      /> */}
      {socialAuthProviders && socialAuthProviders.length > 0 && (
        <Flex direction={"column"} justifyContent="center" alignSelf="center">
          <Text textAlign={"center"} p={4}>
            or sign up with
          </Text>
          <Flex
            direction="row"
            justifyContent="center"
            alignSelf="center"
            gap="2rem"
          >
            {socialAuthProviders.map((provider) => (
              <Button
                key={provider.providerName}
                variant="ghost"
                p="0.5rem"
                minW="auto"
                onClick={() => {
                  const verifyPath = `/social/${provider.providerName}/verify`;
                  const origin = window?.location?.origin || "";
                  const nextParam = (router.query.next as string) || "";
                  const next = nextParam
                    ? `${verifyPath}?next=${encodeURIComponent(nextParam)}`
                    : verifyPath;
                  startSocialSignIn(provider.providerName, `${origin}${next}`);
                }}
              >
                <Image
                  src={provider.providerSvg}
                  alt={provider.providerName}
                  boxSize="24px"
                />
              </Button>
            ))}
          </Flex>
        </Flex>
      )}

      <Button
        isLoading={isLoading || false}
        isDisabled={isLoading || false}
        mt="1rem"
        w="full"
        type="submit"
      >
        Sign Up
      </Button>
      <Text fontWeight={500} mt="1.5rem" textAlign={"center"} w="full">
        {" "}
        Password will be sent via email{" "}
      </Text>
      <Flex
        display={{
          base: "none",
          md: "flex",
        }}
        w="full"
        flexDirection="column"
      >
        <Divider my="2rem" />
        <Text mx="auto" mb="2rem" fontWeight={500}>
          Already have an account?{" "}
          <Link
            href="/login"
            color="primary.500"
            _hover={{
              color: "primary.700",
            }}
          >
            Login
          </Link>
        </Text>
      </Flex>
    </AppForm>
  );
}

export default RegisterForm2;
